import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { Form, Button, Select, Row, Col, Input, DatePicker, message, Switch, } from 'antd';
import { useI18n, useSession } from 'applib-frontend';
// eslint-disable-next-line import/no-unresolved
import { apiV2 } from '@newtontechnologies/beey-api-js-client/endpoints';
import { useCookies } from 'react-cookie';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import 'dayjs/locale/pl';
import 'dayjs/locale/en';
import 'dayjs/locale/de';
import 'dayjs/locale/sk';
import 'dayjs/locale/hr';
import 'dayjs/locale/sl';
import csCZ from 'antd/es/date-picker/locale/cs_CZ';
import plPL from 'antd/es/date-picker/locale/pl_PL';
import enUS from 'antd/es/date-picker/locale/en_US';
import deDE from 'antd/es/date-picker/locale/de_DE';
import skSK from 'antd/es/date-picker/locale/sk_SK';
import slSI from 'antd/es/date-picker/locale/sl_SI';
import hrHR from 'antd/es/date-picker/locale/hr_HR';
import './style.less';
// eslint-disable-next-line import/no-unresolved
import { json } from '@newtontechnologies/beey-api-js-client/receivers';
const { Option } = Select;
const StreamForm = ({ onNewStream }) => {
    var _a, _b, _c;
    const { connection } = useSession();
    const { intl } = useI18n();
    const [form] = Form.useForm();
    const [displayUrl, setDisplayUrl] = useState(false);
    const [cookies, setCookie] = useCookies(['lang']);
    const [channels, setChannels] = useState({});
    const [profiles, setProfiles] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState(cookies.lang !== undefined ? cookies.lang : intl.locale);
    let localeCalendar;
    const sortedLanguageOptions = useMemo(() => {
        const displayNames = new Intl.DisplayNames([intl.locale], { type: 'language' });
        const options = profiles.map((profile) => {
            var _a;
            const parts = profile.lang.split('-');
            const baseLangCode = parts[0];
            const regionCode = parts.length > 1 ? parts[1] : null;
            let languageName = (_a = displayNames.of(baseLangCode)) !== null && _a !== void 0 ? _a : baseLangCode;
            languageName = languageName.charAt(0).toUpperCase() + languageName.slice(1);
            const displayName = regionCode !== null
                ? `${languageName} (${regionCode})`
                : languageName;
            return {
                code: profile.lang,
                name: displayName,
            };
        });
        options.sort((a, b) => a.name.localeCompare(b.name, intl.locale));
        return options;
    }, [profiles, intl.locale]);
    const polishChannels = [
        channels.TVPInfo,
        channels.Jedynka,
        channels['Dwójka'],
        channels['Trójka'],
        channels['Czwórka'],
        channels.TVPInfo,
        channels.PR24,
        channels.rmfFM,
    ];
    const czechChannels = [
        channels['ČT 24'],
        channels['CNN Prima'],
        channels['ČRo Radiožurnál'],
    ];
    const slovakChannels = [
        channels.TA3,
    ];
    const getTranscriptionProfile = (language, profs) => {
        var _a;
        const profilesList = (_a = profs.find((p) => p.lang === language)) === null || _a === void 0 ? void 0 : _a.profiles;
        if (profilesList === undefined || profilesList.length === 0) {
            return '';
        }
        const defaultProfile = profilesList.find((p) => p.profile === 'default');
        if (defaultProfile !== undefined) {
            return defaultProfile.profile;
        }
        return profilesList[0].profile;
    };
    useEffect(() => {
        const fetchChannels = async () => {
            const channelsUrls = await connection.fetch()
                .receive(json())
                .url('api/stream2beey/SupportedUrls')
                .fetch();
            setChannels(channelsUrls);
        };
        const fetchTranscriptionProfiles = async () => {
            var _a;
            const transcriptionProfiles = await connection.fetch()
                .receive(json())
                .url(apiV2.currentUser.transcriptionProfiles.url({ purpose: 'appstream' }))
                .fetch();
            const profilesMap = {};
            transcriptionProfiles.forEach((transcriptionProfile) => {
                const { lang, profile, tags } = transcriptionProfile;
                if (tags.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    if (profilesMap[lang] === undefined) {
                        profilesMap[lang] = [];
                    }
                    profilesMap[lang].push({ profile, tag: tags[0] });
                }
            });
            const profilesArray = Object.entries(profilesMap).map(([lang, profs]) => ({ lang, profiles: profs }));
            setProfiles(profilesArray);
            const lang = (_a = cookies.lang) !== null && _a !== void 0 ? _a : intl.locale;
            form.setFieldsValue({
                language: lang,
                transcriptionProfile: getTranscriptionProfile(lang, profilesArray),
            });
        };
        void fetchChannels();
        void fetchTranscriptionProfiles();
    }, []);
    switch (intl.locale) {
        case 'cs':
            localeCalendar = csCZ;
            csCZ.lang.now = intl.formatMessage({ id: 'currentTime' });
            break;
        case 'pl':
            localeCalendar = plPL;
            plPL.lang.now = intl.formatMessage({ id: 'currentTime' });
            break;
        case 'de':
            localeCalendar = deDE;
            deDE.lang.now = intl.formatMessage({ id: 'currentTime' });
            break;
        case 'sk':
            localeCalendar = skSK;
            skSK.lang.now = intl.formatMessage({ id: 'currentTime' });
            break;
        case 'hr':
            localeCalendar = hrHR;
            hrHR.lang.now = intl.formatMessage({ id: 'currentTime' });
            break;
        case 'sl':
            localeCalendar = slSI;
            slSI.lang.now = intl.formatMessage({ id: 'currentTime' });
            break;
        default:
            localeCalendar = enUS;
            enUS.lang.now = intl.formatMessage({ id: 'currentTime' });
    }
    const saveLanguageToCookies = (language) => {
        setCookie('lang', language, {
            path: '/',
            expires: new Date(Date.now() + 1000000000),
        });
    };
    const handleSelectChannel = (value) => {
        setDisplayUrl(value === 'customUrl');
        if (polishChannels.includes(value)) {
            form.setFieldsValue({ language: 'pl-PL' });
            saveLanguageToCookies('pl-PL');
        }
        else if (czechChannels.includes(value)) {
            form.setFieldsValue({ language: 'cs-CZ' });
            saveLanguageToCookies('cs-CZ');
        }
        else if (slovakChannels.includes(value)) {
            form.setFieldsValue({ language: 'sk-SK' });
            saveLanguageToCookies('sk-SK');
        }
        else if (value === channels.N1INFO) {
            saveLanguageToCookies('sr-RS');
            form.setFieldsValue({ language: 'sr-RS' });
        }
    };
    const isURL = (url) => {
        const re = /^(http|https|rtmp):\/\/[^#%+,]{6,300}(#.*)?$/;
        return re.test(url);
    };
    const uploadTask = async () => {
        const channel = form.getFieldValue('channel');
        const date = form.getFieldValue('date');
        const transcriptionProfile = form.getFieldValue('transcriptionProfile');
        const selectedDate = dayjs(date);
        const currentDate = dayjs();
        const dateLocal = selectedDate.isBefore(currentDate, 'minute') ? currentDate.toISOString() : dayjs(date).toISOString();
        const url = form.getFieldValue('url');
        const name = form.getFieldValue('name');
        const hours = Number(form.getFieldValue('hours'));
        const minutes = Number(form.getFieldValue('minutes'));
        const withDiarization = Boolean(form.getFieldValue('withDiarization'));
        const length = `${hours}:${minutes}`;
        const [streamUrl, custom] = channel === 'customUrl' ? [encodeURIComponent(url), true] : [channel, false];
        if (hours === 0 && minutes === 0) {
            void message.error(`${intl.formatMessage({ id: 'durationAlert' })}`);
        }
        else {
            await connection.fetch()
                .post()
                .url(`api/stream2beey/stream?startDate=${dateLocal}&length=${length}&streamUrl=${streamUrl}&name=${name}&language=${cookies.lang !== undefined ? cookies.lang : intl.locale}&custom=${custom}&withDiarization=${withDiarization}&transcriptionProfile=${transcriptionProfile}`)
                .send();
            onNewStream();
            form.resetFields(['channel', 'url', 'name', 'hours', 'minutes']);
            form.setFieldsValue({ date: dayjs() });
            setDisplayUrl(false);
        }
    };
    const camelCaseToWords = (str) => str
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/(^|\s)\S/g, (t) => t.toUpperCase());
    return (_jsxs("div", { className: "panel", children: [_jsx("h1", { children: intl.formatMessage({ id: 'dataNeeded' }) }), _jsxs(Form, { onFinish: () => uploadTask(), form: form, initialValues: {
                    date: dayjs(),
                    hours: 1,
                    minutes: 0,
                }, children: [_jsxs(Row, { children: [_jsx(Col, { sm: 6, xs: 7, children: _jsxs("p", { children: [_jsx("span", { className: "asterisk", children: "*" }), ' ', "Stream:"] }) }), _jsx(Col, { md: 6, sm: 16, xs: 16, children: _jsx(Form.Item, { name: "channel", rules: [
                                        {
                                            required: true,
                                            message: intl.formatMessage({ id: 'requiredField' }),
                                        },
                                    ], children: _jsxs(Select, { onChange: handleSelectChannel, placeholder: intl.formatMessage({ id: 'selectChannel' }), children: [_jsx(Option, { value: "customUrl", children: intl.formatMessage({ id: 'customUrl' }) }), Object.entries(channels).map(([key, value]) => (_jsx(Option, { value: value, children: camelCaseToWords(key) }, key)))] }) }) }), _jsx(Col, { md: { span: 3, offset: 1 }, sm: 6, xs: 7, className: "lang-input", children: _jsxs("p", { children: [' ', intl.formatMessage({ id: 'language' }), ":"] }) }), _jsx(Col, { md: 6, sm: 16, xs: 16, children: _jsx(Form.Item, { name: "language", children: _jsx(Select, { showSearch: true, placeholder: intl.formatMessage({ id: 'language' }), optionFilterProp: "children", filterOption: (input, option) => {
                                            var _a;
                                            return ((_a = option === null || option === void 0 ? void 0 : option.children) !== null && _a !== void 0 ? _a : '')
                                                .toString().toLowerCase().includes(input.toLowerCase());
                                        }, onChange: (language) => {
                                            setSelectedLanguage(language);
                                            saveLanguageToCookies(language);
                                            form.setFieldsValue({
                                                language,
                                                transcriptionProfile: getTranscriptionProfile(language, profiles),
                                            });
                                        }, children: sortedLanguageOptions.map((option) => (_jsx(Select.Option, { value: option.code, children: option.name }, option.code))) }) }) })] }), ((_b = (_a = profiles.find((item) => item.lang === selectedLanguage)) === null || _a === void 0 ? void 0 : _a.profiles.length) !== null && _b !== void 0 ? _b : 0) > 1 && (_jsxs(Row, { children: [_jsx(Col, { sm: 6, xs: 7, children: _jsxs("p", { children: [_jsx("span", { className: "asterisk", children: "*" }), ' ', intl.formatMessage({ id: 'transcriptionProfile' }), ":"] }) }), _jsx(Col, { md: 17, sm: 16, xs: 16, className: "custom-input", children: _jsx(Form.Item, { name: "transcriptionProfile", rules: [
                                        {
                                            required: true,
                                            message: intl.formatMessage({ id: 'requiredField' }),
                                        },
                                    ], children: _jsx(Select, { placeholder: intl.formatMessage({ id: 'selectProfile' }), id: "transcriptionProfile", children: (_c = profiles
                                            .find((item) => item.lang === selectedLanguage)) === null || _c === void 0 ? void 0 : _c.profiles.map((option) => (_jsx(Select.Option, { value: option.profile, children: option.tag }, option.profile))) }) }) })] })), displayUrl ? (_jsxs(Row, { children: [_jsx(Col, { sm: 6, xs: 7, children: _jsxs("p", { children: [_jsx("span", { className: "asterisk", children: "*" }), ' ', intl.formatMessage({ id: 'customUrl' }), ":"] }) }), _jsx(Col, { md: 17, sm: 16, xs: 16, className: "custom-input", children: _jsx(Form.Item, { name: "url", rules: [
                                        {
                                            required: true,
                                            message: intl.formatMessage({ id: 'requiredField' }),
                                        },
                                        {
                                            validator: async (_, value) => {
                                                if (value === undefined || value === null) {
                                                    return Promise.reject();
                                                }
                                                if (isURL(value)) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(intl.formatMessage({ id: 'invalidURL' }));
                                            },
                                        },
                                    ], children: _jsx(Input, { placeholder: "https://" }) }) })] })) : null, _jsxs(Row, { children: [_jsx(Col, { sm: 6, xs: 7, style: { paddingRight: '5px' }, children: _jsxs("p", { children: [_jsx("span", { className: "asterisk", children: "*" }), ' ', intl.formatMessage({ id: 'startTime' }), ":"] }) }), _jsx(Col, { md: 6, sm: 16, xs: 16, children: _jsx(Form.Item, { name: "date", rules: [
                                        {
                                            required: true,
                                            message: intl.formatMessage({ id: 'requiredField' }),
                                        },
                                        {
                                            validator: async (_, value) => {
                                                if (value === null) {
                                                    return Promise.reject();
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ], children: _jsx(DatePicker, { showTime: { format: 'HH:mm' }, format: ['DD/MM/YYYY  HH:mm'], placeholder: intl.formatMessage({ id: 'start' }), showNow: true, locale: localeCalendar }) }) }), _jsx(Col, { md: { span: 3, offset: 1 }, sm: 6, xs: 7, children: _jsxs("p", { children: [_jsx("span", { className: "asterisk", children: "*" }), ' ', intl.formatMessage({ id: 'length' }), ":"] }) }), _jsx(Col, { md: 2, sm: 3, xs: 4, children: _jsx(Form.Item, { name: "hours", rules: [
                                        {
                                            required: true,
                                            message: intl.formatMessage({ id: 'requiredField' }),
                                        },
                                    ], children: _jsx(Input, { type: "number", min: "0", max: "23" }) }) }), _jsx(Col, { md: 2, sm: 3, xs: 4, style: { paddingLeft: '5px' }, children: _jsx("p", { children: intl.formatMessage({ id: 'hours' }) }) }), _jsx(Col, { md: 2, sm: 3, xs: 4, children: _jsx(Form.Item, { name: "minutes", rules: [
                                        {
                                            required: true,
                                            message: intl.formatMessage({ id: 'requiredField' }),
                                        },
                                    ], children: _jsx(Input, { type: "number", max: "59", min: "0" }) }) }), _jsx(Col, { md: 2, sm: 3, xs: 4, style: { paddingLeft: '5px' }, children: _jsx("p", { children: intl.formatMessage({ id: 'minutes' }) }) })] }), _jsxs(Row, { children: [_jsx(Col, { sm: 6, xs: 7, children: _jsxs("p", { children: [_jsx("span", { className: "asterisk", children: "*" }), ' ', intl.formatMessage({ id: 'taskName' }), ":"] }) }), _jsx(Col, { md: 17, sm: 16, xs: 16, className: "custom-input", children: _jsx(Form.Item, { name: "name", rules: [
                                        {
                                            required: true,
                                            message: intl.formatMessage({ id: 'requiredField' }),
                                        },
                                    ], children: _jsx(Input, { type: "text", placeholder: intl.formatMessage({ id: 'enterTaskName' }) }) }) })] }), _jsxs(Row, { children: [_jsx(Col, { sm: 6, xs: 7, children: _jsxs("p", { children: [_jsx("span", { className: "asterisk", children: "*" }), ' ', intl.formatMessage({ id: 'splitBySpeakers' }), ":"] }) }), _jsx(Col, { md: 17, sm: 16, xs: 16, className: "custom-input", children: _jsx(Form.Item, { name: "withDiarization", children: _jsx(Switch, { size: "small" }) }) }), _jsx(Col, { xs: 24, className: "btn-form", children: _jsx(Button, { htmlType: "submit", children: intl.formatMessage({ id: 'run' }) }) })] })] })] }));
};
export default StreamForm;
